<script>
  import L from 'leaflet';
  import 'leaflet/dist/leaflet.css';
  import { onMount, onDestroy } from 'svelte';
  import UA from 'ua-parser-js';
  const ua = new UA();
  let isMobile = ua.getResult().device['type'] == 'mobile';

  import MapScripting from './MapScripting.svelte';
  import {
    getImageMapMaxZoom,
    getImageMapMinZoomAfterFit,
    IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR,
    IMAGE_MAP_ZOOM_DELTA,
    IMAGE_MAP_ZOOM_SNAP,
    resolveImageMapMaxZoom,
    resolveImageMapMinZoom,
  } from './mapImageZoom.js';
  import {
    applyImageMapCenterPan,
    parseImageMapCenter,
    syncImageMapMaxBoundsForZoom,
  } from './mapImageCoords.js';

  export let mapComponent;
  export let tour;
  export let expanded;
  /** Ignored for image maps — Mapbox zoom levels are not valid for Leaflet CRS.Simple. */
  export let zoom;
  export let mobileLocationsOpen = false;
  export let onCloseMobileLocations = () => {};
  export let focusLocationsFilterOnOpen = false;
  export let onFocusLocationsFilterDone = () => {};
  export let showLocationsMenu = true;
  /** Incremented by Tour when map intro Start should fly to the first Map Menu item. */
  export let goToFirstLocationTrigger = 0;

  let mapContainer;
  let mapInstance = null;
  /** Tracks last `expanded` so we can refit when opening full map after starting on a scene (minimap). */
  let previousExpanded = undefined;

  $: if (expanded !== null && mapInstance) {
    const m = mapInstance;
    const expanding = expanded === true && previousExpanded === false;
    const collapsing = expanded === false && previousExpanded === true;

    m.invalidateSize();
    // Refit immediately on expand/collapse so we do not paint one frame at the wrong zoom
    // (e.g. minimap cover zoom before contain + imageMapMinZoom is applied).
    if (expanding || collapsing) {
      if (typeof m.fitImageMapToView === 'function') {
        m.fitImageMapToView();
      }
    }
    if (expanding) {
      if (typeof m.fitImageMapToView === 'function') {
        requestAnimationFrame(() => m.fitImageMapToView());
        setTimeout(() => m.fitImageMapToView(), 50);
        setTimeout(() => m.fitImageMapToView(), 350);
      }
    } else if (collapsing) {
      if (typeof m.fitImageMapToView === 'function') {
        requestAnimationFrame(() => m.fitImageMapToView());
        setTimeout(() => m.fitImageMapToView(), 50);
      }
    }
    previousExpanded = expanded;
  }

  onMount(() => {
    const url = tour?.mapbox?.mapImageUrl;
    if (!mapContainer || !url) {
      console.error('Image map: container or mapImageUrl missing');
      return;
    }

    const w = tour?.mapImage?.width;
    const h = tour?.mapImage?.height;
    const mb = tour?.mapbox;
    const computedMax =
      w > 0 || h > 0 ? getImageMapMaxZoom(w, h) : 18;
    const maxZoom = resolveImageMapMaxZoom(computedMax, mb?.imageMapMaxZoom);

    const map = L.map(mapContainer, {
      crs: L.CRS.Simple,
      minZoom: IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR,
      maxZoom,
      zoom: 0,
      zoomSnap: IMAGE_MAP_ZOOM_SNAP,
      zoomDelta: IMAGE_MAP_ZOOM_DELTA,
      attributionControl: false,
      zoomControl: false,
    });
    map._isLeafletImageMap = true;

    let aspectWOverH = 1;
    if (w > 0 && h > 0) {
      aspectWOverH = w / h;
    }
    let bounds = L.latLngBounds(L.latLng(0, 0), L.latLng(1 / aspectWOverH, 1));
    map._imageAspectWOverH = aspectWOverH;
    const overlay = L.imageOverlay(url, bounds).addTo(map);

    const syncBoundsFromZoom = () => {
      // Padded maxBounds + tour minZoom are for the full map. On the minimap they
      // clamp getBoundsZoom (cover) and pan — clear them until expanded again.
      if (!expanded) {
        map.setMaxBounds(null);
        return;
      }
      syncImageMapMaxBoundsForZoom(L, map, aspectWOverH);
    };
    map.on('zoomend', syncBoundsFromZoom);

    const fit = () => {
      map.invalidateSize();
      const pad = 2;
      const padding = L.point(pad, pad);

      let fitZoom;
      if (expanded) {
        // Full map: show entire image (object-fit: contain).
        // Reset floor so fitBounds is not clamped by the minimap or a prior imageMapMinZoom.
        map.setMaxBounds(null);
        map.setMinZoom(IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR);
        map.fitBounds(bounds, { animate: false, padding: [pad, pad] });
        fitZoom = map.getZoom();
      } else {
        // Minimap: fill the frame; crop overflow (object-fit: cover).
        // Must reset minZoom first — Leaflet clamps getBoundsZoom to getMinZoom(),
        // and the expanded map leaves a high floor from imageMapMinZoom.
        map.setMaxBounds(null);
        map.setMinZoom(IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR);
        const coverZoom = map.getBoundsZoom(bounds, true, padding);
        if (Number.isFinite(coverZoom)) {
          map.setView(bounds.getCenter(), coverZoom, { animate: false });
          fitZoom = map.getZoom();
        } else {
          map.fitBounds(bounds, { animate: false, padding: [pad, pad] });
          fitZoom = map.getZoom();
        }
        map.setMinZoom(IMAGE_MAP_MIN_ZOOM_FOR_CONSTRUCTOR);
      }

      if (expanded) {
        const computedMin = getImageMapMinZoomAfterFit(fitZoom, 1);
        const maxZ = map.getMaxZoom();
        const minZ = resolveImageMapMinZoom(computedMin, mb?.imageMapMinZoom, maxZ);
        const initZ =
          mb?.imageMapInitialZoom != null &&
          !Number.isNaN(Number(mb.imageMapInitialZoom))
            ? Number(mb.imageMapInitialZoom)
            : null;
        // Apply zoom floor in one step — setMinZoom alone would zoom in after fitBounds
        // and flash the wider “contain” view when imageMapMinZoom is above fit zoom.
        const targetZ =
          initZ != null
            ? Math.min(maxZ, Math.max(minZ, initZ))
            : Math.min(maxZ, Math.max(fitZoom, minZ));
        if (map.getZoom() !== targetZ) {
          map.setZoom(targetZ, { animate: false });
        }
        map.setMinZoom(minZ);
        const startCenter = parseImageMapCenter(mb?.imageMapCenter);
        if (startCenter) {
          applyImageMapCenterPan(L, map, startCenter, aspectWOverH);
        }
      }

      syncBoundsFromZoom();
    };
    map.fitImageMapToView = fit;
    overlay.on('load', () => {
      const el = overlay.getElement?.();
      if (!(w > 0 || h > 0) && el?.naturalWidth > 0 && el?.naturalHeight > 0) {
        aspectWOverH = el.naturalWidth / el.naturalHeight;
        bounds = L.latLngBounds(L.latLng(0, 0), L.latLng(1 / aspectWOverH, 1));
        overlay.setBounds(bounds);
        map._imageAspectWOverH = aspectWOverH;
        const c = getImageMapMaxZoom(el.naturalWidth, el.naturalHeight);
        map.setMaxZoom(resolveImageMapMaxZoom(c, mb?.imageMapMaxZoom));
      }
      fit();
    });
    const img = overlay.getElement?.();
    if (img && img.complete && img.naturalWidth) fit();
    requestAnimationFrame(fit);
    setTimeout(fit, 50);
    setTimeout(fit, 200);

    const ro = new ResizeObserver(() => {
      if (!map) return;
      map.invalidateSize();
      if (typeof map.fitImageMapToView === 'function') {
        requestAnimationFrame(() => map.fitImageMapToView());
      }
    });
    ro.observe(mapContainer);

    mapInstance = map;
    mapComponent = map;

    return () => {
      map.off('zoomend', syncBoundsFromZoom);
      ro.disconnect();
    };
  });

  onDestroy(() => {
    if (mapInstance) {
      mapInstance.remove();
      mapInstance = null;
    }
  });
</script>

<div
  class="map map-image"
  bind:this={mapContainer}
  role="button"
  tabindex="0"
  on:click={() => {
    if (!expanded && window.CC?.switchView) {
      window.CC.switchView();
    }
  }}
  on:keydown={(e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !expanded && window.CC?.switchView) {
      e.preventDefault();
      window.CC.switchView();
    }
  }}
>
  {#if mapInstance}
    <MapScripting
      {tour}
      {expanded}
      map={mapInstance}
      showLocationsMenu={showLocationsMenu}
      {mobileLocationsOpen}
      {onCloseMobileLocations}
      focusLocationsFilterOnOpen={focusLocationsFilterOnOpen}
      onFocusLocationsFilterDone={onFocusLocationsFilterDone}
      {goToFirstLocationTrigger}
    />
  {/if}
</div>

<style>
  :global(.leaflet-container) {
    z-index: 1;
  }
</style>
